import { AxiosResponse } from "axios";
import {
  LessonEntity,
  LessonEntityInput,
  SearchLessonParams,
  PaginatedLessonsResponse,
} from "./lessons.types";
import { api } from "../api";
import { LessonMinimalResponse, LessonResponse } from "../../types/api/getLesson";
import {
  HomeworkResolutionDto,
  NOT_SAME_EXERCISE_ERROR,
  ResourceHomeworkImpact,
} from "../../types/api/resourceHomeworkImpact";

export const getLessons = async (params?: SearchLessonParams) => {
  try {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.pageSize)
      searchParams.append("pageSize", params.pageSize.toString());
    if (params?.lessonTitle)
      searchParams.append("lessonTitle", params.lessonTitle);
    if (params?.courseTitle)
      searchParams.append("courseTitle", params.courseTitle);
    if (params?.lessonId)
      searchParams.append("lessonId", params.lessonId.toString());

    const url = `/lessons${searchParams.toString() ? `?${searchParams.toString()}` : ""
      }`;
    const response: AxiosResponse<PaginatedLessonsResponse> = await api.get(
      url
    );

    return response.status === 200
      ? response.data
      : {
        data: [],
        total: 0,
        page: 1,
        pageSize: 30,
        totalPages: 0,
      };
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const getAllLessons = async () => {
  try {
    const response: AxiosResponse<LessonMinimalResponse[]> = await api.get(
      "/lessons/all"
    );
    return response.status === 200 ? response.data : [];
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const getLessonById = async (id: number) => {
  try {
    const response: AxiosResponse<LessonResponse> = await api.get(
      "/lessons/" + id
    );
    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const createLesson = async (data: LessonEntityInput) => {
  try {
    const response: AxiosResponse<LessonEntity> = await api.post(
      "/lessons",
      data
    );

    return response.status === 201 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

// Thrown when the backend rejects the PATCH because the lesson changed since
// it was loaded (409 LESSON_MODIFIED), so callers can offer a reload instead
// of a generic failure.
export class LessonConflictError extends Error {
  readonly currentUpdatedAt: string | null;

  constructor(message: string, currentUpdatedAt: string | null) {
    super(message);
    // es5 target breaks the prototype chain of Error subclasses
    Object.setPrototypeOf(this, LessonConflictError.prototype);
    this.name = "LessonConflictError";
    this.currentUpdatedAt = currentUpdatedAt;
  }
}

const RESOURCE_HOMEWORK_CONFLICT_TOKENS = [
  "RESOURCE_HAS_HOMEWORKS",
  "RESOURCE_HAS_IN_FLIGHT_ATTEMPTS",
] as const;

type ResourceHomeworkConflictToken =
  (typeof RESOURCE_HOMEWORK_CONFLICT_TOKENS)[number];

// SCRUM-1020: thrown when a lesson save is blocked because the removed or
// replaced resource still has live homeworks. Distinct from
// LessonConflictError so callers can offer the resolution dialog instead of
// a reload prompt.
export class ResourceHomeworkConflictError extends Error {
  readonly token: ResourceHomeworkConflictToken;
  readonly resourceImpact: ResourceHomeworkImpact[];

  constructor(
    message: string,
    token: ResourceHomeworkConflictToken,
    resourceImpact: ResourceHomeworkImpact[]
  ) {
    super(message);
    Object.setPrototypeOf(this, ResourceHomeworkConflictError.prototype);
    this.name = "ResourceHomeworkConflictError";
    this.token = token;
    this.resourceImpact = resourceImpact;
  }
}

// SCRUM-1590: the backend refused a repoint marked as the same exercise
// because the replacement is of another type or has another quiz. The text
// is ours: the backend message is not meant for the admin.
export class NotSameExerciseError extends Error {
  readonly resourceId: number | null;

  constructor(resourceId: number | null) {
    super(
      "El recurso nuevo es de otro tipo o tiene otro quiz, así que no puede ser el mismo ejercicio. Vuelve a guardar y elige «Es otro contenido»."
    );
    Object.setPrototypeOf(this, NotSameExerciseError.prototype);
    this.name = "NotSameExerciseError";
    this.resourceId = resourceId;
  }
}

const isResourceHomeworkConflictToken = (
  value: unknown
): value is ResourceHomeworkConflictToken =>
  RESOURCE_HOMEWORK_CONFLICT_TOKENS.includes(value as ResourceHomeworkConflictToken);

export const updateLesson = async (
  id: number,
  data: LessonEntityInput & {
    expected_updated_at?: string;
    planning_summary_start?: string;
    planning_summary_middle?: string;
    planning_summary_closing?: string;
    planning_super_summary_start?: string;
    planning_super_summary_middle?: string;
    planning_super_summary_closing?: string;
    homework_resolutions?: HomeworkResolutionDto[];
  }
) => {
  try {
    const response: AxiosResponse<LessonEntity> = await api.patch(
      "/lessons/" + id,
      data
    );

    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    // Status alone is the contract: `code`/`error` are only extra
    // confirmation when present, since the backend fix and this client
    // deploy independently. Both keys are read because LESSON_MODIFIED
    // carries `code` while the resource guard carries `error`.
    if (error?.response?.status === 409) {
      const body = error.response.data ?? {};
      const discriminator = body.code ?? body.error;
      if (isResourceHomeworkConflictToken(discriminator)) {
        throw new ResourceHomeworkConflictError(
          body.message ?? "El recurso tiene tareas asignadas",
          discriminator,
          body.resource_impact ?? []
        );
      }
      throw new LessonConflictError(
        body.message ?? "Lesson was modified since it was loaded",
        body.current_updated_at ?? null
      );
    }
    if (
      error?.response?.status === 400 &&
      error.response.data?.error === NOT_SAME_EXERCISE_ERROR
    ) {
      throw new NotSameExerciseError(error.response.data.resource_id ?? null);
    }
    throw error;
  }
};

export const deleteLesson = async (id: number) => {
  try {
    const response: AxiosResponse<LessonEntity> = await api.delete(
      "/lessons/" + id
    );

    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};
